import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { Input } from '@/components/ui/Input';
import { useDocumentInfo, useSaveDocumentInfo } from '@/hooks/useDocumentInfo';
import { parseDanishCivilDate } from '@/lib/danishDate';
import {
  DOCUMENT_INFO_TEXT_MAX,
  documentDateFromSelection,
  isDocumentInfoSchemaError,
  normalizeDocumentInfoText,
  type DocumentInfoField,
} from '@/lib/documentInfo';
import { quotePreview } from '@/utils/fileHighlights';

export interface DocumentInfoSeed {
  field: DocumentInfoField;
  quote: string;
  id: number;
}

interface DocumentInfoDraft {
  title: string;
  author: string;
  date: string;
}

export function DocumentInfoAssignButtons({
  disabled = false,
  onAssign,
}: {
  disabled?: boolean;
  onAssign: (field: DocumentInfoField) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fp-assign">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled}
        data-testid="document-info-assign-title"
        onClick={() => onAssign('title')}
      >
        {t('documents.info.assign_title')}
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled}
        data-testid="document-info-assign-date"
        onClick={() => onAssign('date')}
      >
        {t('documents.info.assign_date')}
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled}
        data-testid="document-info-assign-author"
        onClick={() => onAssign('author')}
      >
        {t('documents.info.assign_author')}
      </Button>
    </div>
  );
}

function sameDraft(a: DocumentInfoDraft, b: DocumentInfoDraft): boolean {
  return a.title === b.title && a.author === b.author && a.date === b.date;
}

export function FileDocumentInfo({
  documentId,
  selectionQuote,
  seed,
  onAssign,
}: {
  documentId: string;
  selectionQuote: string | null;
  seed: DocumentInfoSeed | null;
  onAssign: (field: DocumentInfoField) => void;
}) {
  const { t } = useTranslation();
  const query = useDocumentInfo(documentId);
  const save = useSaveDocumentInfo();
  const server = query.data?.status === 'ready' ? query.data : null;
  const [schemaMissing, setSchemaMissing] = useState(false);
  const blocked = schemaMissing || query.data?.status === 'schema_missing';
  const [title, setTitle] = useState<string | null>(null);
  const [author, setAuthor] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [dateHint, setDateHint] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [appliedSeed, setAppliedSeed] = useState<number | null>(null);

  const titleRef = useRef(title);
  const authorRef = useRef(author);
  const dateRef = useRef(date);
  const serverRef = useRef(server);
  const blockedRef = useRef(blocked);
  const documentIdRef = useRef(documentId);
  const mutateRef = useRef(save.mutateAsync);
  const tRef = useRef(t);
  const ackRef = useRef<DocumentInfoDraft | null>(null);
  const pendingRef = useRef(false);
  const inFlightRef = useRef(false);
  const rerunRef = useRef(false);
  const mountedRef = useRef(true);
  const flushRef = useRef<() => Promise<void>>(async () => undefined);

  titleRef.current = title;
  authorRef.current = author;
  dateRef.current = date;
  serverRef.current = server;
  blockedRef.current = blocked;
  documentIdRef.current = documentId;
  mutateRef.current = save.mutateAsync;
  tRef.current = t;

  flushRef.current = async () => {
    if (blockedRef.current) return;
    const serverNow = serverRef.current;
    if (!serverNow) {
      pendingRef.current = true;
      return;
    }
    if (inFlightRef.current) {
      rerunRef.current = true;
      return;
    }
    inFlightRef.current = true;
    pendingRef.current = false;
    const translate = tRef.current;
    try {
      while (true) {
        rerunRef.current = false;
        const latest = serverRef.current ?? serverNow;
        const base = ackRef.current ?? {
          title: latest.title,
          author: latest.author,
          date: latest.date,
        };
        const draft: DocumentInfoDraft = {
          title: normalizeDocumentInfoText(titleRef.current ?? base.title),
          author: normalizeDocumentInfoText(authorRef.current ?? base.author),
          date: dateRef.current ?? base.date,
        };
        if (
          draft.title.length > DOCUMENT_INFO_TEXT_MAX ||
          draft.author.length > DOCUMENT_INFO_TEXT_MAX
        ) {
          if (mountedRef.current) {
            setSaveState('idle');
            setFieldError(translate('documents.info.too_long', { max: DOCUMENT_INFO_TEXT_MAX }));
          }
          break;
        }
        const civilDate = parseDanishCivilDate(draft.date);
        if (!civilDate) {
          if (mountedRef.current) {
            setSaveState('idle');
            setFieldError(translate('documents.info.date_invalid'));
          }
          break;
        }
        const ready: DocumentInfoDraft = { ...draft, date: civilDate };
        const storedOnServer =
          ready.title === latest.title &&
          ready.author === latest.author &&
          ready.date === latest.date;
        if (storedOnServer || (ackRef.current != null && sameDraft(ackRef.current, ready))) {
          break;
        }
        if (mountedRef.current) {
          setFieldError(null);
          setSaveState('saving');
        }
        try {
          await mutateRef.current({
            documentId: documentIdRef.current,
            title: ready.title,
            author: ready.author,
            date: ready.date,
          });
        } catch (error) {
          if (isDocumentInfoSchemaError(error)) {
            if (mountedRef.current) setSchemaMissing(true);
            break;
          }
          if (mountedRef.current) {
            setSaveState('idle');
            setFieldError(translate('documents.info.save_failed'));
          }
          if (rerunRef.current) continue;
          break;
        }
        ackRef.current = ready;
        const titleStill =
          normalizeDocumentInfoText(titleRef.current ?? ready.title) === ready.title;
        const authorStill =
          normalizeDocumentInfoText(authorRef.current ?? ready.author) === ready.author;
        const dateStill = (dateRef.current ?? ready.date) === ready.date;
        if (titleStill) titleRef.current = ready.title;
        if (authorStill) authorRef.current = ready.author;
        if (dateStill) dateRef.current = ready.date;
        if (mountedRef.current) {
          if (titleStill) setTitle(ready.title);
          if (authorStill) setAuthor(ready.author);
          if (dateStill) setDate(ready.date);
          setFieldError(null);
          setSaveState('saved');
        }
        if (!rerunRef.current) break;
      }
    } finally {
      inFlightRef.current = false;
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      void flushRef.current();
    };
  }, []);

  useEffect(() => {
    if (!server || blocked || !pendingRef.current) return;
    pendingRef.current = false;
    void flushRef.current();
  }, [server, blocked]);

  useEffect(() => {
    if (!seed || appliedSeed === seed.id) return;
    setAppliedSeed(seed.id);
    setFieldError(null);
    const text = normalizeDocumentInfoText(seed.quote);
    if (seed.field === 'title') {
      titleRef.current = text;
      setTitle(text);
      setSaveState('idle');
      void flushRef.current();
      return;
    }
    if (seed.field === 'author') {
      authorRef.current = text;
      setAuthor(text);
      setSaveState('idle');
      void flushRef.current();
      return;
    }
    const parsed = documentDateFromSelection(seed.quote);
    if (!parsed) {
      setDateHint(t('documents.info.date_unparsed'));
      return;
    }
    setDateHint(null);
    dateRef.current = parsed;
    setDate(parsed);
    setSaveState('idle');
    void flushRef.current();
  }, [appliedSeed, seed, t]);

  const shownTitle = title ?? server?.title ?? '';
  const shownAuthor = author ?? server?.author ?? '';
  const shownDate = date ?? server?.date ?? '';

  const markEditing = () => {
    setFieldError(null);
    setSaveState((current) => (current === 'saved' ? 'idle' : current));
  };

  return (
    <section
      className="fp-info"
      role="tabpanel"
      id="fp-rail-info-panel"
      aria-labelledby="fp-rail-tab-info"
      data-testid="file-document-info"
    >
      {blocked ? (
        <p className="fp-info-warning" role="status" data-testid="document-info-schema-warning">
          {t('documents.info.schema_warning')}
        </p>
      ) : (
        <p className="fp-info-hint">{t('documents.info.hint')}</p>
      )}

      {selectionQuote && !blocked ? (
        <div className="fp-assign-block">
          <p className="fp-field-label">{t('documents.info.selection_label')}</p>
          <p className="fp-quote">{quotePreview(selectionQuote)}</p>
          <DocumentInfoAssignButtons onAssign={onAssign} />
        </div>
      ) : null}

      {query.isLoading && !server ? (
        <p className="fp-list-state">{t('documents.info.loading')}</p>
      ) : null}

      {query.isError && !blocked ? (
        <div className="fp-list-state">
          <p>{t('documents.info.load_error')}</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => void query.refetch()}>
            {t('documents.info.retry')}
          </Button>
        </div>
      ) : null}

      {server || blocked ? (
        <div className="fp-info-fields">
          <Input
            id="document-info-title"
            label={t('documents.info.title')}
            value={shownTitle}
            disabled={blocked}
            maxLength={DOCUMENT_INFO_TEXT_MAX}
            onChange={(event) => {
              titleRef.current = event.target.value;
              setTitle(event.target.value);
              markEditing();
            }}
            onBlur={(event) => {
              const next = normalizeDocumentInfoText(event.currentTarget.value);
              titleRef.current = next;
              setTitle(next);
              void flushRef.current();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
            data-testid="document-info-title"
          />
          <DanishDateField
            name="document-info-date"
            label={t('documents.info.date')}
            value={shownDate}
            disabled={blocked}
            calendar
            onChange={(next) => {
              dateRef.current = next;
              setDate(next);
              setDateHint(null);
              setFieldError(null);
              void flushRef.current();
            }}
          />
          {dateHint ? <p className="fp-form-error">{dateHint}</p> : null}
          <Input
            id="document-info-author"
            label={t('documents.info.author')}
            value={shownAuthor}
            disabled={blocked}
            maxLength={DOCUMENT_INFO_TEXT_MAX}
            onChange={(event) => {
              authorRef.current = event.target.value;
              setAuthor(event.target.value);
              markEditing();
            }}
            onBlur={(event) => {
              const next = normalizeDocumentInfoText(event.currentTarget.value);
              authorRef.current = next;
              setAuthor(next);
              void flushRef.current();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
            data-testid="document-info-author"
          />
          <p className="fp-info-hint">{t('documents.info.filename_hint')}</p>
          {saveState === 'idle' ? null : (
            <p className="fp-info-hint" role="status" data-testid="document-info-status">
              {saveState === 'saving' ? t('documents.info.saving') : t('documents.info.saved')}
            </p>
          )}
          {fieldError ? <p className="fp-form-error">{fieldError}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
